import { CustomError, ErrorCode } from 'src/error';
import { retryFetch } from './retryFetch';

export interface FetchEndpointOpts {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: any;
}

export async function fetchEndpointData<T>(url: string | URL, opts: FetchEndpointOpts = {}): Promise<T> {
  const method = (opts.method ?? 'GET').toUpperCase();
  const urlString = url.toString();
  const shouldCache = method === 'GET';

  // We manually cache as the browser is very aggressive with removing cache entries
  if (shouldCache) {
    try {
      const cachedData = await getCachedData<T>(urlString);

      if (cachedData) {
        console.log('Cache hit', urlString);
        return cachedData;
      }
    } catch (err) {
      console.warn('Error accessing IndexedDB cache:', err);
      // Continue with fetch if cache access fails
    }
  }

  const { response, error } = await retryFetch(url, {
    method: opts.method ?? 'GET',
    body: opts.body
      ? JSON.stringify(opts.body, (_, v) => {
          if (typeof v === 'bigint') {
            return v.toString();
          }

          return v;
        })
      : undefined,
    headers: {
      ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
      Accept: 'application/json',
    },
  });

  if (error) {
    throw error;
  }

  if (!response) {
    throw new Error(`No response received from ${urlString}`);
  }

  const json = await readJsonResponse(response);
  if (!response.ok) {
    // // Check for auth errors
    // if (response.status === 401) {
    //   const clientAuthState = getClientAuthState();
    //   clientAuthState.triggerAuthCheck();
    // }

    let errorCode = ErrorCode.UNEXPECTED_ERROR;
    switch (response.status) {
      case 400:
        errorCode = ErrorCode.VALIDATION_ERROR;
        break;
      case 401:
        errorCode = ErrorCode.UNAUTHORIZED;
        break;
      case 403:
        errorCode = ErrorCode.PERMISSION_DENIED;
        break;
      case 404:
        errorCode = ErrorCode.NOT_FOUND;
        break;
      case 429:
        errorCode = ErrorCode.RATE_LIMIT_EXCEEDED;
        break;
      case 500:
        errorCode = ErrorCode.UNEXPECTED_ERROR;
        break;
      default:
        errorCode = ErrorCode.UNEXPECTED_ERROR;
        break;
    }

    const serverError = getServerErrorPayload(json);
    if (serverError) {
      if (typeof serverError === 'object' && typeof serverError.message === 'string') {
        const customError = new CustomError(serverError.message, errorCode);
        if (Array.isArray(serverError.validation)) {
          customError.validation = serverError.validation;
        }
        throw customError;
      }

      if (typeof serverError === 'string') {
        throw new CustomError(serverError, errorCode);
      }
    }

    if (response.status === 400) {
      throw new CustomError('Invalid request', errorCode);
    }

    if (response.status === 401) {
      throw new CustomError('Unauthorized access - please log in', errorCode);
    }

    if (response.status === 403) {
      throw new CustomError('You do not have access to this resource', errorCode);
    }

    if (response.status === 404) {
      throw new CustomError('Resource not found', errorCode);
    }

    if (response.status === 429) {
      throw new CustomError('Too many requests', errorCode);
    }

    if (response.status >= 500) {
      throw new CustomError('Server error', errorCode);
    }

    throw new CustomError(response.statusText ? `Request failed: ${response.statusText}` : 'Request failed', errorCode);
  }

  // Cache the successful response
  if (shouldCache) {
    try {
      const maxAge = getMaxAge(response.headers.get('Cache-Control'));

      if (maxAge) {
        await cacheData(urlString, json, maxAge);
      }
    } catch (err) {
      console.warn('Error saving to IndexedDB cache:', err);
      // Continue anyway as the fetch was successful
    }
  }

  return json as T;
}

interface ServerErrorObject {
  message?: string;
  validation?: unknown[];
}

function getServerErrorPayload(json: unknown): string | ServerErrorObject | undefined {
  if (!json || typeof json !== 'object') {
    return undefined;
  }

  const error = (json as { error?: unknown }).error;
  if (typeof error === 'string') {
    return error;
  }

  if (error && typeof error === 'object') {
    const errorObject = error as ServerErrorObject;
    return {
      message: typeof errorObject.message === 'string' ? errorObject.message : undefined,
      validation: Array.isArray(errorObject.validation) ? errorObject.validation : undefined,
    };
  }

  return undefined;
}

async function readJsonResponse(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) {
    return undefined;
  }

  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

function getMaxAge(cacheControl: string | null): number | undefined {
  if (!cacheControl) {
    return undefined;
  }

  // Don't cache if no-store is specified
  if (cacheControl.includes('no-store')) {
    return undefined;
  }

  // Don't cache if no-cache is specified
  if (cacheControl.includes('no-cache')) {
    return undefined;
  }

  const maxAgeMatch = cacheControl.match(/max-age=(\d+)/);
  if (maxAgeMatch && maxAgeMatch[1]) {
    return parseInt(maxAgeMatch[1], 10);
  }

  return undefined;
}

// IndexedDB helper functions
const DB_NAME = 'apiCache';
const STORE_NAME = 'responses';
const DB_VERSION = 1;

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expires: number;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = globalThis.indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'url' });
      }
    };
  });
}

async function getCachedData<T>(url: string): Promise<T | null> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(url);

    request.onerror = () => reject(request.error);

    request.onsuccess = () => {
      const entry = request.result as CacheEntry<T> | undefined;

      if (!entry) {
        resolve(null);
        return;
      }

      // If we have an explicit expiration time, use that
      if (entry.expires != null && Date.now() > entry.expires) {
        resolve(null);
        return;
      }

      resolve(entry.data);
    };
  });
}

async function cacheData<T>(url: string, data: T, maxAge: number): Promise<void> {
  const db = await openDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const entry: CacheEntry<T> & { url: string } = {
      url,
      data,
      timestamp: Date.now(),
      expires: Date.now() + maxAge * 1000,
    };

    const request = store.put(entry);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}
